import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import { ReferralInput, submitReferral } from '../../services/partnerPortal';
import { Alert, inputClass } from '../partner-network/ui';

const emptyForm: ReferralInput = {
  businessName: '',
  businessType: '',
  country: '',
  city: '',
  website: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  problem: '',
  additionalInformation: '',
  identifiedHow: '',
  estimatedValue: '',
};

const SubmitReferral: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<ReferralInput>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ message: string; id: string } | null>(null);

  const set = <K extends keyof ReferralInput>(key: K, value: ReferralInput[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.businessName.trim()) {
      setError('The business name is required.');
      return;
    }
    if (!form.problem.trim()) {
      setError('Tell us what problem the business is experiencing.');
      return;
    }
    if (!form.contactEmail?.trim() && !form.contactPhone?.trim()) {
      setError('Give us either an email or a phone number so we can reach them.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await submitReferral({
        ...form,
        businessName: form.businessName.trim(),
        problem: form.problem.trim(),
        estimatedValue:
          form.estimatedValue === '' || form.estimatedValue === undefined
            ? undefined
            : Number(form.estimatedValue),
      });
      setDone({ message: response.message, id: response.referral.id });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit the referral');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="py-12">
        <AnimatedSection animation="slide-up" className="mx-auto max-w-lg px-4">
          <div className="rounded-2xl border border-surface-border bg-surface-card p-8 text-center">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-primary-500/40 bg-primary-500/10">
              <svg className="h-7 w-7 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="mb-3 font-display text-2xl font-semibold text-white">
              Referral submitted
            </h1>
            <p className="mb-8 text-sm text-muted">{done.message}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setForm(emptyForm);
                  setDone(null);
                }}
                className="btn-secondary py-2.5 px-5"
              >
                Submit another
              </button>
              <button
                type="button"
                onClick={() => navigate(`/partner/referrals/${done.id}`)}
                className="btn-primary py-2.5 px-5"
              >
                Track this referral
              </button>
            </div>
          </div>
        </AnimatedSection>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/partner/referrals"
          className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M15 19l-7-7 7-7" />
          </svg>
          My referrals
        </Link>

        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
              Submit a referral
            </h1>
            <p className="text-sm text-muted">
              Tell us about the business and what they are struggling with. We take it from
              there and keep you posted at every stage.
            </p>
          </div>
        </AnimatedSection>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-2xl border border-surface-border bg-surface-card p-6 md:p-8"
        >
          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted">
              Business information
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-2 block text-sm text-muted">Business name *</label>
                <input
                  type="text"
                  value={form.businessName}
                  onChange={(e) => set('businessName', e.target.value)}
                  placeholder="ABC Restaurant"
                  autoFocus
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Business type</label>
                <input
                  type="text"
                  value={form.businessType}
                  onChange={(e) => set('businessType', e.target.value)}
                  placeholder="Restaurant, hotel, retail…"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Website</label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => set('website', e.target.value)}
                  placeholder="https://"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Country</label>
                <input
                  type="text"
                  value={form.country}
                  onChange={(e) => set('country', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">City</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted">
              Who should we speak to?
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-2 block text-sm text-muted">Contact person</label>
                <input
                  type="text"
                  value={form.contactName}
                  onChange={(e) => set('contactName', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Email</label>
                <input
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) => set('contactEmail', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">Phone</label>
                <input
                  type="tel"
                  value={form.contactPhone}
                  onChange={(e) => set('contactPhone', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
            <p className="mt-3 text-xs text-muted">
              We need at least one of these to be able to reach them.
            </p>
          </section>

          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted">
              The opportunity
            </h2>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm text-muted">
                  What problem are they experiencing? *
                </label>
                <textarea
                  value={form.problem}
                  onChange={(e) => set('problem', e.target.value)}
                  rows={4}
                  placeholder="Orders are taken on paper, stock is guesswork, and they have no idea which items actually make money."
                  className={`${inputClass} resize-y`}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">
                  Anything else we should know?
                </label>
                <textarea
                  value={form.additionalInformation}
                  onChange={(e) => set('additionalInformation', e.target.value)}
                  rows={3}
                  className={`${inputClass} resize-y`}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-muted">
                  How did you identify this opportunity?
                </label>
                <input
                  type="text"
                  value={form.identifiedHow}
                  onChange={(e) => set('identifiedHow', e.target.value)}
                  placeholder="Long-standing client, met at an event, …"
                  className={inputClass}
                />
              </div>
              <div className="sm:max-w-xs">
                <label className="mb-2 block text-sm text-muted">
                  Estimated project value (₦)
                </label>
                <input
                  type="number"
                  min={0}
                  value={form.estimatedValue}
                  onChange={(e) =>
                    set('estimatedValue', e.target.value === '' ? '' : Number(e.target.value))
                  }
                  className={inputClass}
                />
                <p className="mt-2 text-xs text-muted">
                  A rough guess is fine. Commission is calculated from the value actually
                  agreed, not this figure.
                </p>
              </div>
            </div>
          </section>

          <div className="flex justify-end gap-3 border-t border-surface-border pt-6">
            <Link to="/partner/referrals" className="btn-secondary py-2.5 px-5">
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary py-2.5 px-5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Submit referral'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default SubmitReferral;
